/**
 * Practice Mode Stats Storage
 * Stores practice game history in localStorage (not wallet-dependent)
 */

export interface PracticeMatchEntry {
  timestamp: number;
  rank: number;
  totalPlayers: number;
  score: number;
}

export interface PracticeStats {
  gamesPlayed: number;
  wins: number; // 1st place finishes
  highScore: number;
  totalScore: number;
  bestRank: number; // Best placement (1 is best)
  matchHistory: PracticeMatchEntry[];
}

const STORAGE_KEY = 'mine-or-die-practice-stats';
const MAX_HISTORY_ENTRIES = 50;

/**
 * Load practice stats from localStorage
 */
export function loadPracticeStats(): PracticeStats {
  const stored = localStorage.getItem(STORAGE_KEY);

  if (stored) {
    try {
      return JSON.parse(stored);
    } catch (e) {
      console.error('Failed to parse practice stats:', e);
    }
  }

  return {
    gamesPlayed: 0,
    wins: 0,
    highScore: 0,
    totalScore: 0,
    bestRank: 0,
    matchHistory: [],
  };
}

/**
 * Save practice stats to localStorage
 */
export function savePracticeStats(stats: PracticeStats): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(stats));
}

/**
 * Record a practice match result
 */
export function recordPracticeMatch(
  rank: number,
  totalPlayers: number,
  score: number
): PracticeStats {
  const stats = loadPracticeStats();

  const entry: PracticeMatchEntry = {
    timestamp: Date.now(),
    rank,
    totalPlayers,
    score,
  };

  stats.matchHistory.unshift(entry);

  if (stats.matchHistory.length > MAX_HISTORY_ENTRIES) {
    stats.matchHistory = stats.matchHistory.slice(0, MAX_HISTORY_ENTRIES);
  }

  stats.gamesPlayed++;
  stats.totalScore += score;

  if (score > stats.highScore) {
    stats.highScore = score;
  }

  if (stats.bestRank === 0 || rank < stats.bestRank) {
    stats.bestRank = rank;
  }

  if (rank === 1) {
    stats.wins++;
  }

  savePracticeStats(stats);
  return stats;
}

/**
 * Get average score
 */
export function getAverageScore(stats: PracticeStats): number {
  if (stats.gamesPlayed === 0) return 0;
  return Math.round(stats.totalScore / stats.gamesPlayed);
}

/**
 * Get win rate as percentage
 */
export function getWinRate(stats: PracticeStats): number {
  if (stats.gamesPlayed === 0) return 0;
  return Math.round((stats.wins / stats.gamesPlayed) * 100);
}

/**
 * Clear practice stats (for testing)
 */
export function clearPracticeStats(): void {
  localStorage.removeItem(STORAGE_KEY);
}
